import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { t } from '../i18n'
import { logSearchQuery, logSearchResult, useSearch } from '../Hooks/useSearch'
import { CloseIcon, SearchIcon, Spinner } from './Icons'
import { SourceAvatar } from './SourceAvatar'

export function searchResultsPath(query) {
  return `/pretraga?q=${encodeURIComponent(query.trim())}`
}

/* The groups of a search answer: topics, categories, sources (only groups
   with a hit). Shared by the desktop dropdown and mobile Istraži. */
export function SearchGroups({ data, query, onOpen }) {
  const open = (type, position) => () => {
    logSearchResult(query, data, type, position)
    onOpen?.(type)
  }
  return (
    <>
      {data.topics.length > 0 && (
        <div className="search-group">
          <div className="search-group-title">{t('search.topics')}</div>
          {data.topics.map((topic, i) => (
            <Link
              key={topic.slug}
              to={`/tema/${topic.slug}`}
              state={{ entry: 'search' }}
              className="search-row"
              onClick={open('topic', i + 1)}
            >
              <span className="search-row-text">
                <span className="search-row-name">{topic.shortTitle || topic.title}</span>
                <span className="search-row-meta">
                  {t('search.topicMeta', { count: topic.articleCount ?? topic.storyCount })}
                </span>
              </span>
            </Link>
          ))}
        </div>
      )}
      {data.categories.length > 0 && (
        <div className="search-group">
          <div className="search-group-title">{t('search.categories')}</div>
          {data.categories.map((c, i) => (
            <Link
              key={c.slug}
              to={`/kategorija/${c.slug}`}
              state={{ entry: 'search' }}
              className="search-row"
              onClick={open('category', i + 1)}
            >
              <span className="search-row-text">
                <span className="search-row-name">{c.title}</span>
                <span className="search-row-meta">{t('search.categoryMeta')}</span>
              </span>
            </Link>
          ))}
        </div>
      )}
      {data.sources.length > 0 && (
        <div className="search-group">
          <div className="search-group-title">{t('search.sources')}</div>
          {data.sources.map((s, i) => (
            <Link
              key={s.slug}
              to={`/izvor/${s.slug}`}
              state={{ entry: 'search' }}
              className="search-row"
              onClick={open('source', i + 1)}
            >
              <SourceAvatar source={s} size={28} />
              <span className="search-row-name">{s.name}</span>
            </Link>
          ))}
        </div>
      )}
    </>
  )
}

/* Desktop right column (spec 5.8): always ready to type; a click into the
   empty field only highlights it; results open while typing and close on X,
   Escape, a click outside or an empty field. */
export default function SearchBox() {
  const navigate = useNavigate()
  const [value, setValue] = useState('')
  const [open, setOpen] = useState(false)
  const box = useRef(null)
  const input = useRef(null)
  const { query, data, loading } = useSearch(value)

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => box.current && !box.current.contains(e.target) && setOpen(false)
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const close = () => setOpen(false)
  const reset = () => {
    setValue('')
    setOpen(false)
  }
  const submit = (e) => {
    e.preventDefault()
    if (value.trim().length < 2) return
    logSearchQuery(value, data)
    reset()
    navigate(searchResultsPath(value))
  }

  const text = value.trim()
  const empty = data && !data.topics.length && !data.categories.length && !data.sources.length

  return (
    <div className="search-box" ref={box}>
      <form role="search" onSubmit={submit} className="search-field">
        <SearchIcon size={18} className="search-field-icon" />
        <input
          ref={input}
          type="search"
          value={value}
          placeholder={t('search.placeholder')}
          aria-label={t('search.label')}
          autoComplete="off"
          onChange={(e) => {
            setValue(e.target.value)
            setOpen(e.target.value.trim().length > 0)
          }}
          onFocus={() => value.trim() && setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              close()
              input.current?.blur()
            }
          }}
        />
        {value && (
          <button
            type="button"
            className="icon-button search-clear"
            aria-label={t('common.clear')}
            onClick={() => {
              reset()
              input.current?.focus()
            }}
          >
            <CloseIcon size={16} />
          </button>
        )}
      </form>

      {open && text && (
        <div className="search-panel" role="region" aria-label={t('search.results')}>
          <div className="search-panel-title">
            {t('search.results')}
            {loading && <Spinner size={14} />}
          </div>
          {text.length >= 2 && (
            <Link
              to={searchResultsPath(text)}
              className="search-row search-row-news"
              onClick={() => {
                logSearchQuery(text, data)
                reset()
              }}
            >
              <SearchIcon size={18} />
              <span className="search-row-name">{t('search.searchNewsFor', { query: text })}</span>
            </Link>
          )}
          {data && <SearchGroups data={data} query={text} onOpen={reset} />}
          {empty && !loading && <p className="search-empty">{t('search.noResults', { query })}</p>}
          <Link to="/izvori" className="search-row search-row-all" onClick={reset}>
            {t('search.allSources')}
          </Link>
        </div>
      )}
    </div>
  )
}
